import { CloudUpload } from "lucide-react";
import React, { useContext, useRef } from "react";
import {
  Group,
  MantineTheme,
  useMantineTheme,
  Text,
  Space,
  Button,
} from "@mantine/core";
import { Dropzone, DropzoneStatus, MIME_TYPES } from "@mantine/dropzone";
import { Navigate } from "react-router-dom";
import { DEFAULT_EMPLOYEE_CONFIG } from "../utils/constants";
import { AuthContext } from "../../../providers/AuthProvider";
import EmployeeImportFileUploadStyles from "../assets/EmployeeImportFileUploadStyles";

function getActiveColor(status: DropzoneStatus, theme: MantineTheme): string {
  if (status.accepted) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return theme.colors[theme.primaryColor][6];
  }

  if (status.rejected) {
    return theme.colors.red[6];
  }

  return theme.black;
}

const getStatus = (status: DropzoneStatus): string => {
  if (status.accepted) {
    return "Drop files here";
  }

  if (status.rejected) {
    return "Pdf file less than 30mb";
  }

  return "Upload file";
};

type EmployeeImportFileUploadProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  onDone: () => void;
};

const EmployeeImportFileUpload: React.FC<EmployeeImportFileUploadProps> = ({
  form,
  onDone,
}) => {
  const theme = useMantineTheme();
  const { classes } = EmployeeImportFileUploadStyles();
  const openRef = useRef<() => void>(null);
  const auth = useContext(AuthContext);
  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <div className={classes.wrapper}>
      <Dropzone
        openRef={openRef}
        onDrop={(files) => {
          form.setFieldValue("file", files[0]);
          new Response(files[0]).text().then((res) => {
            const headers = res.slice(0, res.indexOf("\r\n")).split(",");
            form.setFieldValue(
              "columnHeaders",
              headers.map((name: string) => {
                const isFound = DEFAULT_EMPLOYEE_CONFIG.find((it) =>
                  Object.is(name, it.value)
                );
                return {
                  name,
                  relatedColumn: isFound ? isFound.value : null,
                  isTag: false,
                };
              })
            );
            onDone();
          });
        }}
        className={classes.dropzone}
        radius="md"
        accept={[MIME_TYPES.csv]}
        maxSize={30 * 1024 ** 2}
      >
        {(status) => (
          <div style={{ pointerEvents: "none" }}>
            <Group position="center">
              <CloudUpload size={50} color={getActiveColor(status, theme)} />
            </Group>
            <Text
              align="center"
              weight={700}
              size="lg"
              mt="xl"
              sx={{ color: getActiveColor(status, theme) }}
            >
              {getStatus(status)}
            </Text>
            <Text align="center" size="sm" mt="xs" color="dimmed">
              Drag and drop a CSV file here to upload employee data or select a
              file from your computer to upload.
            </Text>
          </div>
        )}
      </Dropzone>

      <Space h="xl" />
      <Button
        type="button"
        variant="default"
        size="md"
        radius="xl"
        color="jem-theme"
        onClick={() => {
          if (openRef && openRef.current) {
            openRef.current();
          }
        }}
      >
        Upload CSV
      </Button>
    </div>
  );
};

export default EmployeeImportFileUpload;
